import type { useToast } from 'primevue/usetoast';
import type { OperationVariables, QueryOptions } from '@apollo/client/core';

/**
 * Generic function which is bound to the grapqhl
 * types to ensure type safety as in @/node_modules/@apollo/client/core/ApolloClient.d.ts
 */
export default async function <
  T = any,
  TVariables extends OperationVariables = OperationVariables
>(
  queryOptions: QueryOptions<TVariables, T>,
  errorMessage: string = "Es ist ein Fehler aufgetreten",
  showErrorToast: boolean = true
) {
  const nuxtApp = useNuxtApp();
  const getToast: typeof useToast = () =>
    nuxtApp.vueApp.config.globalProperties.$toast;
  const toastService = getToast();

  const { resolveClient } = useApolloClient();
  const apolloClient = resolveClient();

  try {
    return await apolloClient.query<T>({
      ...queryOptions,
    });
  } catch (error: any) {
    // Check if it's a 401 authentication error
    const is401 = error?.networkError?.statusCode === 401 ||
                  error?.graphQLErrors?.some((err: any) =>
                    err.extensions?.code === 'UNAUTHENTICATED' ||
                    err.message?.includes('Unauthorized')
                  );

    if (is401) {
      console.debug('[storeGraphqlQuery] 401 error detected, attempting token refresh');
      const tokenRefresh = useTokenRefresh();
      const refreshed = await tokenRefresh.refreshNow();

      if (refreshed) {
        console.debug('[storeGraphqlQuery] Token refreshed, retrying query');
        // Retry the query after token refresh
        try {
          return await apolloClient.query<T>({
            ...queryOptions,
          });
        } catch (retryError) {
          console.error('[storeGraphqlQuery] Retry failed:', retryError);
          if (showErrorToast) {
            setTimeout(() => {
              toastService.add({
                severity: "error",
                summary: errorMessage,
                life: 3000,
              });
            }, 3000);
          }
          throw retryError;
        }
      } else {
        // Token refresh failed, user will be redirected to log in by useTokenRefresh
        console.warn('[storeGraphqlQuery] Token refresh failed');
        throw error;
      }
    }

    // For non-401 errors, show error toast
    if (!showErrorToast) return;
    setTimeout(() => {
      toastService.add({
        severity: "error",
        summary: errorMessage,
        life: 3000,
      });
    }, 3000);

    throw error;
  }
}
